import { z } from 'zod'

const quantity = z
  .union([z.number(), z.string().trim().min(1)])
  .transform(value => (typeof value === 'string' ? Number(value) : value))
  .pipe(z.number().finite().nonnegative())

const nullableString = z
  .string()
  .nullish()
  .transform(value => value ?? null)

const nullablePositiveInteger = z
  .number()
  .int()
  .positive()
  .nullish()
  .transform(value => value ?? null)

export const JitWorkItemStatusSchema = z.enum([
  'WAITING_FOR_MAPPING',
  'WAITING_FOR_CLASSIFICATION',
  'WAITING_FOR_ACK',
  'NEEDS_ENRICHMENT',
  'READY',
  'DISPATCH_PENDING',
  'BUYER_ACTION_REQUIRED',
  'PARTIALLY_PLACED',
  'PLACED',
  'FAILED',
  'SUPERSEDED',
  'CANCELLED',
])
export type JitWorkItemStatus = z.infer<typeof JitWorkItemStatusSchema>

export const JitLineStatusSchema = z.enum([
  'WAITING_FOR_ACK',
  'NEEDS_INPUT',
  'READY',
  'EXCLUDED',
  'BUYER_PENDING',
  'PLACED',
])
export type JitLineStatus = z.infer<typeof JitLineStatusSchema>

export const JitPurchaseOrderSummarySchema = z.object({
  id: z.string(),
  sourceChannel: z.string(),
  sourcePoNumber: z.string(),
  sourceLineCount: z.number().int().nonnegative(),
  jitLineCount: z.number().int().nonnegative(),
  receivedAt: z.string(),
  cancelBy: nullableString,
  acceptedQtyEaches: quantity,
  requestedQtyEaches: quantity,
  status: JitWorkItemStatusSchema,
  linkedPoCount: z.number().int().nonnegative(),
  linkedPoStatus: nullableString,
  updatedAt: z.string(),
  version: z.number().int().positive(),
})
export type JitPurchaseOrderSummary = z.infer<typeof JitPurchaseOrderSummarySchema>

export const JitPurchaseOrderPageSchema = z.object({
  items: z.array(JitPurchaseOrderSummarySchema),
  page: z.number().int().nonnegative(),
  size: z.number().int().positive(),
  total: z.number().int().nonnegative(),
})
export type JitPurchaseOrderPage = z.infer<typeof JitPurchaseOrderPageSchema>

export const JitWarehouseOptionSchema = z.object({
  code: z.string(),
  name: z.string(),
  bcLocationCode: z.string(),
})
export type JitWarehouseOption = z.infer<typeof JitWarehouseOptionSchema>

export const JitPurchaseOrderLineSchema = z.object({
  id: z.string(),
  itemSequenceNumber: z.number().int().positive(),
  channelSku: z.string(),
  sku: z.string(),
  title: z.string(),
  acceptedQtyEaches: quantity,
  requestedQtyEaches: quantity,
  sourceQuantity: quantity,
  sourceUnitOfMeasure: z.string(),
  unitSize: nullablePositiveInteger,
  warehouseCode: nullableString,
  reference: z.string(),
  status: JitLineStatusSchema,
  vulcanPoIds: z.array(z.string()),
  // Optional product identity, same keys as the PO `OrderItem` line. Vision's JIT
  // line DTO does not emit them yet; Pulse fills the gaps from the channel catalog
  // (`GET /v1/items`). `sku` above is Vision's mapped SKU, which is the GS1/EAN
  // Vulcan receives as `gs1`, so `ean` only matters when the two ever diverge.
  ean: nullableString.optional(),
  displayName: nullableString.optional(),
  imageUrl: nullableString.optional(),
})
export type JitPurchaseOrderLine = z.infer<typeof JitPurchaseOrderLineSchema>

/** Vulcan marketplace JIT plan status (MarketplaceJitPlanState). */
export const JitVulcanPlanStatusSchema = z.enum([
  'pending',
  'partially_placed',
  'placed',
  'superseded',
  'abandoned',
  'cancelled',
])
export type JitVulcanPlanStatus = z.infer<typeof JitVulcanPlanStatusSchema>

const vulcanPlanStatus = z
  .string()
  .transform(value => {
    const key = value.trim().toLowerCase().replace(/-/g, '_')
    if (key === 'pending_buyer') return 'pending'
    return key
  })
  .pipe(JitVulcanPlanStatusSchema)

export const JitVulcanPlanSchema = z.object({
  id: z.string(),
  reason: z.string(),
  status: vulcanPlanStatus,
  createdAt: z.string(),
  pendingLineCount: z.number().int().nonnegative(),
})
export type JitVulcanPlan = z.infer<typeof JitVulcanPlanSchema>

export const JitVulcanPurchaseOrderSchema = z.object({
  id: z.string(),
  poNumber: nullableString,
  vendorName: z.string(),
  reference: z.string(),
  locationCode: z.string(),
  requestedQtyEaches: quantity,
  orderedQtyEaches: quantity,
  status: z.string(),
  bcNumber: nullableString,
  pdfReady: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  externalLineIds: z.array(z.string()).optional(),
})
export type JitVulcanPurchaseOrder = z.infer<typeof JitVulcanPurchaseOrderSchema>

export const JitAuditEventSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  label: z.string(),
  actor: z.string(),
  occurredAt: nullableString,
  source: z.enum(['vision', 'vulcan', 'norn']),
  detail: nullableString,
})
export type JitAuditEvent = z.infer<typeof JitAuditEventSchema>

export const JitPurchaseOrderDetailSchema = JitPurchaseOrderSummarySchema.extend({
  buyerEntity: nullableString,
  sourceStatus: z.string(),
  lines: z.array(JitPurchaseOrderLineSchema),
  warehouseOptions: z.array(JitWarehouseOptionSchema),
  vulcanPlan: JitVulcanPlanSchema.nullish().transform(value => value ?? null),
  vulcanPurchaseOrders: z.array(JitVulcanPurchaseOrderSchema),
  auditEvents: z.array(JitAuditEventSchema),
  auditGaps: z.array(z.string()),
  vulcanSyncedAt: nullableString,
  vulcanDataStale: z.boolean(),
  warnings: z.array(z.string()),
})
export type JitPurchaseOrderDetail = z.infer<typeof JitPurchaseOrderDetailSchema>

export interface JitSubmissionLine {
  lineId: string
  requestedQtyEaches: number
  warehouseCode: string | null
  reference: string
}

export interface JitSubmissionRequest {
  lines: JitSubmissionLine[]
}

export const JitSubmissionResultSchema = z.object({
  workItemId: z.string(),
  status: JitWorkItemStatusSchema,
  version: z.number().int().positive(),
  vulcanPlanId: z.string(),
})
export type JitSubmissionResult = z.infer<typeof JitSubmissionResultSchema>

/**
 * Bulk submit (`POST /v1/jit-purchase-orders/bulk-submissions`).
 *
 * - `perSource` — one Vulcan plan per source work item; each active line carries
 *   the shared reference for that source PO.
 * - `mergeByEan` — one plan; `reference` is header-level; lines are EAN rolls
 *   with contributor line ids.
 */
export const JitBulkSubmitModeSchema = z.enum(['perSource', 'mergeByEan'])
export type JitBulkSubmitMode = z.infer<typeof JitBulkSubmitModeSchema>

export interface JitBulkPerSourceLine {
  workItemId: string
  lineId: string
  requestedQtyEaches: number
  reference: string
}

export interface JitBulkMergedSubmitLine {
  sku: string
  sourcePoNumbers: string[]
  contributorLineIds: string[]
  requestedQtyEaches: number
}

/** Optimistic-concurrency tokens for every source work item in the bulk body. */
export interface JitBulkMember {
  workItemId: string
  version: number
}

export type JitBulkSubmissionRequest =
  | {
      mode: 'perSource'
      warehouseCode: string
      members: JitBulkMember[]
      lines: JitBulkPerSourceLine[]
    }
  | {
      mode: 'mergeByEan'
      warehouseCode: string
      /** One reference for the single merged Vulcan PO. */
      reference: string
      members: JitBulkMember[]
      lines: JitBulkMergedSubmitLine[]
    }

export const JitBulkSubmissionResultSchema = z.object({
  groupId: z.string(),
  /** Present when Vision returns a single primary plan id; prefer vulcanPlanIds. */
  vulcanPlanId: z.string().optional(),
  vulcanPlanIds: z.array(z.string()).min(1),
  workItemIds: z.array(z.string()).min(1),
  status: JitWorkItemStatusSchema,
  mode: JitBulkSubmitModeSchema,
})
export type JitBulkSubmissionResult = z.infer<typeof JitBulkSubmissionResultSchema>

export const JitStaleVersionProblemSchema = z.object({
  title: z.literal('STALE_VERSION'),
  status: z.literal(412),
  currentVersion: z.number().int().positive(),
})
